import axios from 'axios'
import config from 'configuration'

import { isPublicPath } from './publicPaths'

const sharedConfig = {
  baseURL: config.api,
  paramsSerializer: (params) =>
    Object.keys(params)
      .filter((key) => params[key] !== undefined)
      .map((key) => {
        if (Object.prototype.toString.call(params[key]) === '[object Object]') {
          params[key] = JSON.stringify(params[key])
        }
        if (Array.isArray(params[key])) {
          return params[key]
            .map((v) => `${key}[]=${encodeURIComponent(v)}`)
            .join('&')
        }
        return `${key}=${encodeURIComponent(params[key])}`
      })
      .join('&'),
  headers: {
    'Content-Type': 'application/json',
  },
}

// Session-cookie endpoints, which core serves behind an explicit CORS
// allow-list that includes the dashboard's own origins. Exported as the default
// at the bottom of the file, once its interceptor is attached.
const credentialedAxios = axios.create({
  ...sharedConfig,
  withCredentials: true,
})

// Public, bearer-token endpoints (`/api/d2c/*`, `/api/warranty-offers` and
// its `/api/v2/warranty-offers` successor).
// Core answers these with `Access-Control-Allow-Origin: *` because the same
// routes are called from retailer storefronts and the browser extension, and a
// browser will not expose a wildcard response to a credentialed request -- so
// the cookie must not be sent. It was never read server-side (MUL-238).
export const publicAxios = axios.create({
  ...sharedConfig,
  withCredentials: false,
})

// Using the wrong client surfaces as an opaque browser CORS error, raised by
// the browser rather than by us and far from the call that caused it. These
// interceptors turn that into a named failure at the call site. They throw
// outside production, where a wrong client should stop the build; in production
// they only report, because a false positive must not break a page that would
// otherwise have worked.
const assertCorsProfile = (clientIsPublic) => (requestConfig) => {
  const url = requestConfig.url ?? ''
  if (url && isPublicPath(url) !== clientIsPublic) {
    const message = clientIsPublic
      ? `${url} is not a public route on core, so it needs the session cookie, but it was requested through publicAxios. Use the default axios client.`
      : `${url} is a public route on core (wildcard CORS, credentials refused), but it was requested through the credentialed axios client. Use publicAxios.`
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(message)
    }
    // Deliberate: production reports the mismatch rather than throwing, so a
    // false positive cannot break a page that would otherwise have worked.
    // eslint-disable-next-line no-console
    console.error(message)
  }
  return requestConfig
}

// `synchronous: true` keeps axios on its synchronous dispatch path. Without it
// a request interceptor defers every request by a microtask, which silently
// breaks callers -- and tests -- that assume the request is issued in the same
// tick as the call.
const SYNCHRONOUS = { synchronous: true }

// On that synchronous path axios responds to a throwing interceptor by calling
// the rejection handler and then dispatching the request anyway, so the handler
// has to rethrow for the request to actually be stopped. Passing `null` throws
// a confusing `Cannot read properties of null` instead.
const rethrow = (error) => {
  throw error
}

credentialedAxios.interceptors.request.use(
  assertCorsProfile(false),
  rethrow,
  SYNCHRONOUS
)
publicAxios.interceptors.request.use(
  assertCorsProfile(true),
  rethrow,
  SYNCHRONOUS
)

export default credentialedAxios
